import React, { useState, useEffect } from 'react';
import { Box, Button, Chip, TextField, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import useDishStore from '../../store/dishStore';
import useRestaurantStore from '../../store/restaurantStore';
import useIngredientStore from '../../store/ingredientStore'; // Add this import
import DraggableDish from './DraggableDish';
import DraggableRestaurant from './DraggableRestaurant';

const DishSearchPanel = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchType, setSearchType] = useState('all'); // 'all', 'dishes', 'restaurants'
  
  const { dishes, fetchDishes } = useDishStore();
  const { restaurants, fetchRestaurants } = useRestaurantStore();
  const { ingredients, fetchIngredients } = useIngredientStore(); // Add ingredient store
  
  // Define available tags
  const availableTags = [
    { label: t('plans.searchPanel.tags.dimSum'), value: '点心' },
    { label: t('plans.searchPanel.tags.breakfast'), value: '早餐' },
    { label: t('plans.searchPanel.tags.lunch'), value: '午餐' },
    { label: t('plans.searchPanel.tags.dinner'), value: '晚餐' },
    { label: t('plans.searchPanel.tags.soup'), value: '汤类' }
  ];

  // Enhanced filter function for dishes with ingredient search
  const filteredDishes = dishes.filter(dish => {
    // Search in dish name
    const matchesName = dish.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Search in dish description
    const matchesDescription = dish.description && 
      dish.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Search in ingredients
    const matchesIngredients = dish.recipe && dish.recipe.some(ingredient => 
      ingredient.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Combined search match
    const matchesSearch = searchQuery === '' || matchesName || matchesDescription || matchesIngredients;
    
    // Tag filtering
    const matchesTags = selectedTags.length === 0 || 
      selectedTags.some(tag => {
        if (dish.tags) {
          return dish.tags.toLowerCase().includes(tag.toLowerCase());
        }
        return false;
      });
      
    return matchesSearch && matchesTags;
  });

  // Enhanced filter function for restaurants
  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = searchQuery === '' ||
      restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (restaurant.cuisine_type && restaurant.cuisine_type.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (restaurant.address && restaurant.address.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  // Filter ingredients based on search query
  const filteredIngredients = ingredients.filter(ingredient => {
    return searchQuery === '' || 
      ingredient.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Get results based on search type
  const getSearchResults = () => {
    switch (searchType) {
      case 'dishes':
        return { dishes: filteredDishes, restaurants: [], ingredients: [] };
      case 'restaurants':
        return { dishes: [], restaurants: filteredRestaurants, ingredients: [] };
      case 'ingredients':
        return { dishes: [], restaurants: [], ingredients: filteredIngredients };
      default: // 'all'
        return { 
          dishes: filteredDishes, 
          restaurants: filteredRestaurants, 
          ingredients: filteredIngredients 
        };
    }
  };

  const { dishes: resultDishes, restaurants: resultRestaurants, ingredients: resultIngredients } = getSearchResults();

  // Handle tag selection
  const handleTagClick = (tagValue) => {
    setSelectedTags(prev => {
      if (prev.includes(tagValue)) {
        return prev.filter(tag => tag !== tagValue);
      } else {
        return [...prev, tagValue];
      }
    });
  };

  // Handle search type change
  const handleSearchTypeChange = (event, newSearchType) => {
    if (newSearchType !== null) {
      setSearchType(newSearchType);
    }
  };

  // Handle ingredient click to search for dishes with that ingredient
  const handleIngredientClick = (ingredientName) => {
    setSearchQuery(ingredientName);
    setSearchType('dishes'); // Switch to dishes view to show results
  };

  // Make the search panel droppable for deletion
  const {
    isOver,
    setNodeRef
  } = useDroppable({
    id: 'delete-zone',
    data: {
      type: 'delete'
    }
  });
  
  useEffect(() => {
    fetchDishes();
    fetchRestaurants();
    fetchIngredients(); // Fetch ingredients
  }, []);

  const style = {
    backgroundColor: isOver ? '#ffebee' : 'white',
    border: isOver ? '2px dashed #f44336' : '1px solid #ddd',
  };

  const hasActiveFilters = selectedTags.length > 0 || searchQuery;
  const totalResults = resultDishes.length + resultRestaurants.length + resultIngredients.length;

  return (
    <Box 
      ref={setNodeRef}
      style={style}
      sx={{ 
        p: 2, 
        width: 300, 
        borderLeft: '1px solid #ddd',
        minHeight: '80vh'
      }}
    >

      {isOver && (
        <Typography 
          variant="body2" 
          color="error" 
          sx={{ 
            mb: 1, 
            fontStyle: 'italic',
            textAlign: 'center',
            backgroundColor: '#ffcdd2',
            p: 1,
            borderRadius: 1
          }}
        >
          {t('plans.searchPanel.dropToRemove')}
        </Typography>
      )}
      
      {/* Search Input with enhanced placeholder */}
      <TextField
        placeholder={t('plans.searchPanel.searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      {/* Search Type Toggle - Add ingredients option */}
      <ToggleButtonGroup
        value={searchType}
        exclusive
        onChange={handleSearchTypeChange}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all">{t('common.labels.all')}</ToggleButton>
        <ToggleButton value="dishes">{t('plans.searchPanel.dishes')}</ToggleButton>
        <ToggleButton value="restaurants">{t('plans.searchPanel.restaurants')}</ToggleButton>
        <ToggleButton value="ingredients">{t('ingredients.title')}</ToggleButton>
      </ToggleButtonGroup>

      {/* Tag Filter Chips - only show for dishes */}
      {(searchType === 'all' || searchType === 'dishes') && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row', 
          flexWrap: 'wrap',
          gap: 1,
          padding: 1,
          mb: 2
        }}>
          {availableTags.map((tag) => (
            <Chip 
              key={tag.value}
              label={tag.label}
              clickable
              color={selectedTags.includes(tag.value) ? 'primary' : 'default'}
              variant={selectedTags.includes(tag.value) ? 'filled' : 'outlined'}
              onClick={() => handleTagClick(tag.value)}
              sx={{
                '&:hover': {
                  backgroundColor: selectedTags.includes(tag.value) 
                    ? 'primary.dark' 
                    : 'action.hover'
                }
              }}
            />
          ))}
        </Box>
      )}

      {/* Clear filters button */}
      {hasActiveFilters && (
        <Box sx={{ mb: 2 }}>
          <Button 
            size="small" 
            onClick={() => {
              setSelectedTags([]);
              setSearchQuery('');
            }}
            variant="outlined"
            fullWidth
          >
            {t('plans.searchPanel.clearFilters')} ({totalResults} {t('common.labels.results')})
          </Button>
        </Box>
      )}
      
      {/* Results */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1, 
        maxHeight: "60vh", 
        overflowY: 'auto' 
      }}>
        {/* Ingredients Section */}
        {resultIngredients.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              {t('plans.searchPanel.ingredients', { count: resultIngredients.length })}
            </Typography>
            {resultIngredients.map((ingredient) => (
              <Box
                key={`ingredient-${ingredient.id}`}
                sx={{
                  p: 1,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#1976d2'
                  }
                }}
                onClick={() => handleIngredientClick(ingredient.name)}
              >
                <Typography variant="body2" fontWeight="medium">
                  {ingredient.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('plans.searchPanel.clickToFindDishes')}
                </Typography>
              </Box>
            ))}
          </>
        )}

        {/* Restaurants Section */}
        {resultRestaurants.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              {t('plans.searchPanel.restaurants', { count: resultRestaurants.length })}
            </Typography>
            {resultRestaurants.map((restaurant) => (
              <DraggableRestaurant key={`restaurant-${restaurant.id}`} restaurant={restaurant} />
            ))}
          </>
        )}

        {/* Dishes Section */}
        {resultDishes.length > 0 && (
          <>
            {(resultRestaurants.length > 0 || resultIngredients.length > 0) && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                {t('plans.searchPanel.dishes', { count: resultDishes.length })}
              </Typography>
            )}
            {resultDishes.map((dish) => (
              <DraggableDish key={`dish-${dish.id}`} dish={dish} />
            ))}
          </>
        )}

        {/* No results */}
        {totalResults === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {hasActiveFilters ? t('plans.searchPanel.noResultsMatch') : t('plans.searchPanel.noItemsFound')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DishSearchPanel;